import React from 'react'
import Link from 'next/link'
import { FaBell, FaBookOpen, FaChartLine, FaComments, FaFileInvoice, FaLock, FaMobileAlt, FaRobot, FaShieldAlt } from 'react-icons/fa'
import { MdAir } from 'react-icons/md'
import { IoBody } from 'react-icons/io5'
import { BsArrowRight, BsLightningChargeFill } from 'react-icons/bs'
import { Faq } from '../commons/Faq'
import { MarketHead } from './MarketHead'
import { buildContext, guideLinks, localityLinks, resolveAll } from './content'
import {
    CTASection,
    HeroSection,
    LocalServicesSection,
    PaymentSection,
    ServicesSection,
    StatsBanner,
    TechnologySection,
    TrustSectionCustomers,
    TrustSectionPartners,
} from '../index/ModernHomePage'
import { absoluteUrl, breadcrumbNode, faqNode, graph, marketProvider, webPageNode } from '../../utils/market-schema'
import { s3Static } from '../../utils/constants'
import { getPersonalTrainerPackages } from '../../data/PersonalTrainerPackages'
import { AIRCON_SERVICES, PT_TOPICS, marketKey } from '../../data/markets/registry'
import { COUNTRY_HOME_ALTERNATES, airconBookingPath, t } from '../../utils/markets'

// Country home for /ae and /my.
//
// Built from the Singapore homepage's sections (components/index/
// ModernHomePage) so the three country homes share one layout and depth. The
// copy is each country's own (data/markets/<cc>/home.js), never the SG text:
// pages that read alike would compete with /sg in search. Facts stay local
// too - no Singapore job counts, and no Google rating while the country's
// `googleRating` feature is off.
//
// Section order follows /sg: hero -> stats -> services -> per-city service
// copy -> why us -> how booking works -> CTA -> platform & app -> partners ->
// payments (only where online payment is on) -> guides -> FAQ.

const FEATURE_ICONS = [FaRobot, FaChartLine, FaMobileAlt, FaLock, FaShieldAlt, FaBell]
const APP_POINT_ICONS = [FaMobileAlt, FaComments, FaFileInvoice, FaBell]

const SectionHeader = ({ icon: Icon, label, title }) => (
    <div className="text-center mb-16">
        <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Icon />
            {label}
        </span>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">{title}</h2>
    </div>
)

const HowItWorksSection = ({ title, steps }) => (
    <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
            <SectionHeader icon={BsLightningChargeFill} label="HOW IT WORKS" title={title} />
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {steps.map((step, i) => (
                    <li key={i} className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm">
                        <span className="w-12 h-12 bg-linear-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-5 text-xl font-black text-white">
                            {i + 1}
                        </span>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                        <p className="text-slate-600 leading-relaxed">{step.body}</p>
                    </li>
                ))}
            </ol>
        </div>
    </section>
)

const GuidesSection = ({ title, guides }) => (
    <section className="py-20 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4">
            <SectionHeader icon={FaBookOpen} label="GUIDES" title={title} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {guides.map((guide) => (
                    <Link
                        key={guide.href}
                        href={guide.href}
                        prefetch={false}
                        className="group p-6 bg-linear-to-br from-slate-50 to-white border border-slate-100 rounded-2xl hover:shadow-lg transition-all duration-300"
                    >
                        <h3 className="text-lg font-bold text-slate-900 group-hover:text-orange-600 transition-colors">{guide.title}</h3>
                        {guide.body && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{guide.body}</p>}
                        <span className="mt-4 inline-flex items-center gap-1.5 text-orange-600 font-semibold text-sm group-hover:gap-2.5 transition-all">
                            Read the guide
                            <BsArrowRight />
                        </span>
                    </Link>
                ))}
            </div>
        </div>
    </section>
)

export const MarketHomePage = ({ countryCd, tables, modifiedDate, content: raw }) => {
    const ctx = buildContext({ countryCd, citySlug: null, tables })
    const { market, money } = ctx
    const pkgs = getPersonalTrainerPackages(countryCd)
    const lowestPt = Math.min(...pkgs.Signature.packages.map((p) => p.perSessionPrice))
    const content = resolveAll(raw, { ...ctx, lowestPt })
    const path = market.homePath
    const firstCity = market.cities[0]
    const localityCount = market.cities.reduce((n, c) => n + c.localities.length, 0)

    const hubLinks = market.cities.flatMap((city) => [
        { href: market.hubs.aircon[city.slug], title: `${t(market, 'acCategory')} ${city.name}`, body: `${t(market, 'acGeneral')} from ${money(tables.general[0].wall)}` },
        { href: market.hubs.pt[city.slug], title: `Personal Trainer ${city.name}`, body: `From ${money(lowestPt, 0)} a session` },
    ])

    const jsonLd = graph(
        webPageNode({ market, path, title: content.title, description: content.description, dateModified: modifiedDate }),
        breadcrumbNode(path, market.shortName),
        {
            '@type': 'ItemList',
            '@id': `${absoluteUrl(path)}/#services`,
            name: `StringsSG services in ${market.shortName}`,
            itemListElement: hubLinks.map((l, i) => ({ '@type': 'ListItem', position: i + 1, name: l.title, url: absoluteUrl(l.href) })),
        },
        { ...marketProvider, '@id': `${absoluteUrl(path)}/#provider`, areaServed: { '@type': 'Country', name: market.countryName } },
        content.faqs?.length ? faqNode(path, content.faqs) : null,
    )

    const hero = {
        imageAlt: content.heroAlt,
        title: content.hero.title,
        highlight: content.hero.highlight,
        sub: content.hero.sub,
        ctas: [
            { href: airconBookingPath(countryCd), label: `Book ${t(market, 'acCategory')}` },
            { href: market.hubs.pt[firstCity.slug], label: 'Find a Personal Trainer' },
        ],
        trustLine: 'Run by StringsSG Pte Ltd · Home services since 2018',
        badges: content.hero.badges,
    }

    const stats = [
        { value: '2018', label: 'Founded' },
        { value: String(market.cities.length), label: market.cities.length > 1 ? 'Cities' : 'City' },
        { value: `${localityCount}+`, label: 'Areas Covered' },
        { value: money(lowestPt, 0), label: 'PT Sessions From' },
    ]

    const servicesSection = {
        title: content.services.title,
        intro: content.services.intro,
        items: [
            {
                icon: MdAir,
                title: content.services.aircon.title,
                description: content.services.aircon.description,
                ctaText: content.services.aircon.ctaText,
                image: s3Static + 'blog/living/aircon/chinese-aircon-service-man-smiling.jpg',
                imageAlt: content.services.aircon.imageAlt,
                link: market.hubs.aircon[firstCity.slug],
                highlight: `${t(market, 'acGeneral')} from ${money(tables.general[0].wall)}`,
                color: 'from-sky-500 to-blue-600',
            },
            {
                icon: IoBody,
                title: content.services.pt.title,
                description: content.services.pt.description,
                ctaText: content.services.pt.ctaText,
                image: s3Static + 'personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg',
                imageAlt: content.services.pt.imageAlt,
                link: market.hubs.pt[firstCity.slug],
                highlight: `From ${money(lowestPt, 0)} a session`,
                color: 'from-rose-500 to-pink-600',
            },
        ],
    }

    // Per city: an aircon column and a PT column (UAE: Dubai and Abu Dhabi),
    // plus areas and guides columns where the copy provides them (Malaysia).
    const allGuides = market.cities.flatMap((c) => [...guideLinks(market, c.slug, 'aircon'), ...guideLinks(market, c.slug, 'pt')])
    const localColumns = market.cities.flatMap((city) => {
        const copy = content.local[city.slug]
        const key = marketKey(countryCd, city.slug)
        const columns = [
            {
                kind: 'aircon',
                heading: copy.aircon.heading,
                body: copy.aircon.body,
                links: (AIRCON_SERVICES[key] ?? []).slice(0, 5).map((s) => ({ href: `${market.hubs.aircon[city.slug]}/${s.slug}`, label: s.title })),
                hub: { href: market.hubs.aircon[city.slug], label: `${t(market, 'acShort')} services in ${city.name}` },
            },
            {
                kind: 'pt',
                heading: copy.pt.heading,
                body: copy.pt.body,
                links: (PT_TOPICS[key] ?? []).slice(0, 5).map((s) => ({ href: `${market.hubs.pt[city.slug]}/${s.slug}`, label: s.title })),
                hub: { href: market.hubs.pt[city.slug], label: `personal training in ${city.name}` },
            },
        ]
        if (copy.areas) {
            columns.push({
                kind: 'areas',
                heading: copy.areas.heading,
                body: copy.areas.body,
                links: localityLinks(market, city).slice(0, 5).map((l) => ({ href: l.href, label: `${t(market, 'acCategory')} ${l.title}` })),
                hub: { href: market.hubs.aircon[city.slug], label: `areas in ${city.shortName ?? city.name}` },
            })
        }
        if (copy.guides) {
            columns.push({
                kind: 'guides',
                heading: copy.guides.heading,
                body: copy.guides.body,
                links: allGuides.slice(0, 5).map((g) => ({ href: g.href, label: g.title })),
            })
        }
        return columns
    })

    // Service by service (every city's aircon, then every city's PT), the
    // order the SG section uses. sort() is stable, so cities keep their order.
    const KIND_ORDER = ['aircon', 'pt', 'areas', 'guides']
    const localItems = [...localColumns].sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))

    const trust = {
        title: content.trust.title,
        intro: content.trust.intro,
        items: content.trust.items,
        card: {
            title: content.trust.card.title,
            body: content.trust.card.body,
            stats: [
                { value: String(market.cities.length), label: market.cities.length > 1 ? 'Cities' : 'City' },
                { value: `${localityCount}+`, label: 'Areas Covered' },
            ],
            googleReviews: false,
        },
    }

    const technology = {
        intro: content.technology.intro,
        features: content.technology.features.map((f, i) => ({ ...f, icon: FEATURE_ICONS[i % FEATURE_ICONS.length] })),
        appPoints: content.technology.appPoints.map((text, i) => ({ text, icon: APP_POINT_ICONS[i % APP_POINT_ICONS.length] })),
    }

    return (
        <>
            <MarketHead
                market={market}
                path={path}
                title={content.title}
                description={content.description}
                jsonLd={jsonLd}
                alternates={COUNTRY_HOME_ALTERNATES}
                ogImageAlt={content.heroAlt}
            />

            <div className="min-h-screen -mt-14">
                <HeroSection hero={hero} />
                <StatsBanner stats={stats} />
                <ServicesSection section={servicesSection} />
                <LocalServicesSection items={localItems} />
                <TrustSectionCustomers trust={trust} />
                <HowItWorksSection title={content.stepsTitle} steps={content.steps} />
                <CTASection cta={{ title: content.ctaTitle, body: content.ctaBody }} />
                <TechnologySection technology={technology} />
                <TrustSectionPartners partners={content.partners} />
                {market.paymentsEnabled && <PaymentSection footnote={null} />}
                {allGuides.length > 0 && <GuidesSection title={content.guidesTitle} guides={allGuides} />}
                {content.faqs?.length > 0 && (
                    <section className="py-20 md:py-28 bg-slate-50" aria-label="Frequently asked questions">
                        <div className="mx-auto max-w-4xl px-4">
                            <Faq questionsAnswers={content.faqs} title="Questions" showSearch={false} jsonLd={false} />
                        </div>
                    </section>
                )}
            </div>
        </>
    )
}
